// Archies shared predictive-search UX for the -ext search shells.
//
// Serves BOTH the inline mobile burger-drawer search (predictive-search--drawer) AND the
// desktop / mobile-header search modal (predictive-search--modal). Responsibilities:
//   1. Move each shell's shared "Popular searches" pills (search-suggestions-ext) into that
//      component's results content-wrapper so they show as the focus/empty state, and hide them
//      once a query is typed.
//   2. Pills fill their own component's field and run predictive search.
//   3. For the INLINE drawer only (no dialog wrapper), add the missing close affordances — the X
//      button, click-outside, and Escape. The modal is a dialog-component that closes natively, so
//      those are skipped there.

if (!window.__archiesSearchExtWired) {
  window.__archiesSearchExtWired = true;

  const isExpanded = (comp) => comp && comp.classList.contains('predictive-search--expanded');

  // Collapse the inline drawer search: hide the results overlay, clear the query, drop focus.
  const collapse = (comp) => {
    if (!comp) return;
    comp.classList.remove('predictive-search--expanded');
    const input = comp.querySelector('input[type="search"]');
    if (input) {
      if (input.value) {
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      input.blur();
    }
  };

  // Show the pills INSIDE the results content-wrapper (above the results, which predictive-search.js
  // repopulates) so they appear as the empty/focus state, and hide them once a query is typed.
  const setupPills = () => {
    document.querySelectorAll('predictive-search-component.predictive-search--ext').forEach((comp) => {
      const shell = comp.parentElement;
      if (!shell) return;
      const pills = shell.querySelector('.search-suggestions-ext');
      const wrapper = comp.querySelector('.predictive-search-form__content-wrapper');
      const input = comp.querySelector('input[type="search"]');
      if (!pills || !wrapper) return;
      if (pills.parentElement !== wrapper) {
        wrapper.insertBefore(pills, wrapper.firstChild);
      }
      if (input && !input.__archiesPillSync) {
        input.__archiesPillSync = true;
        const sync = () => {
          pills.hidden = input.value.trim().length > 0;
        };
        input.addEventListener('input', sync);
        sync();
      }
      // Drawer only: mark the component "expanded" while the field is focused. The close (X) button,
      // outside-click and Escape affordances are all gated on `--expanded`, but nothing was adding it —
      // so the popover had no way to be dismissed. Focus adds it; collapse() removes it.
      if (comp.classList.contains('predictive-search--drawer') && input && !input.__archiesExpandSync) {
        input.__archiesExpandSync = true;
        input.addEventListener('focus', () => comp.classList.add('predictive-search--expanded'));
      }
    });
  };

  // Pills → fill the field of the pill's OWN component (drawer or modal) + run predictive search.
  document.addEventListener('click', (event) => {
    const pill = event.target.closest('.search-suggestions-ext__pill[data-search-term]');
    if (!pill) return;
    event.preventDefault();
    const comp = pill.closest('predictive-search-component');
    const input = comp && comp.querySelector('input[type="search"]');
    if (!input) return;
    input.value = pill.getAttribute('data-search-term');
    input.focus();
    // Must be an InputEvent with an inputType — predictive-search.js's search handler bails on
    // `if (!event.inputType) return`, so a plain Event('input') fills the field but never searches.
    input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText' }));
    // [Archies] The pill already chose the query, so drop focus to hide the soft keyboard and reveal
    // the full results — same UX for BOTH mobile shells (drawer + header modal). The drawer also needs
    // --expanded re-asserted so its results overlay (+ close X) stays open; blur alone doesn't collapse it.
    if (comp.classList.contains('predictive-search--drawer')) {
      comp.classList.add('predictive-search--expanded');
    }
    input.blur();
    // The component re-focuses the field as it renders results (async), which would re-open the
    // keyboard — blur again after that settles so the keyboard stays down.
    setTimeout(function () { input.blur(); }, 400);
  });

  // ---- Drawer-only close affordances (inline component, no dialog) ----
  const getDrawerComp = () => document.querySelector('predictive-search-component.predictive-search--drawer');

  document.addEventListener('click', (event) => {
    const comp = getDrawerComp();
    if (!isExpanded(comp)) return;

    // Explicit close (X) button.
    if (event.target.closest('.predictive-search--drawer .predictive-search__close-modal-button')) {
      event.preventDefault();
      collapse(comp);
      return;
    }

    // "Close" text in the popover heading row (search-suggestions-ext show_close).
    if (event.target.closest('.predictive-search--drawer [data-search-close]')) {
      event.preventDefault();
      collapse(comp);
      return;
    }

    // Click anywhere outside the drawer search component → collapse (the outside element's own click
    // still proceeds — we don't preventDefault, so tapping a menu tile both closes search + follows it).
    if (!event.target.closest('.predictive-search--drawer')) {
      collapse(comp);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const comp = getDrawerComp();
    if (isExpanded(comp)) collapse(comp);
  });

  // iOS: while the search is expanded (soft keyboard up), the OS drags the page behind to keep the
  // focused input in view — the page scrolls behind the popover even with overflow:hidden on the menu.
  // Block touchmove everywhere WHILE expanded, except inside the results scroller (which must still
  // scroll). passive:false is required so preventDefault() takes effect on touchmove.
  document.addEventListener('touchmove', (event) => {
    const comp = getDrawerComp();
    if (!isExpanded(comp)) return;
    if (event.target && event.target.closest && event.target.closest('.predictive-search--drawer .predictive-search-form__content')) return;
    event.preventDefault();
  }, { passive: false });

  setupPills();
  document.addEventListener('DOMContentLoaded', setupPills);

  // ---- Okendo re-hydration for async predictive-search results ----
  // Each row renders Okendo's live star rating (<div data-oke-star-rating>), which arrives empty
  // because predictive search injects results via morph. Re-init Okendo after each results change
  // when any star-rating is still un-hydrated. Debounced so fast typing doesn't thrash it.
  // (The "View all" button is Horizon's native one — `.predictive-search-form__footer`; we only
  // style it in predictive-search-ext.css, no custom button needed.)
  const rehydrateOkendo = () => {
    const api = window.okeWidgetApi;
    if (!api || typeof api.initAllWidgets !== 'function') return;
    let pending = false;
    document.querySelectorAll('.predictive-search-form__content [data-oke-star-rating]').forEach((el) => {
      if (!el.querySelector('.oke-sr')) pending = true;
    });
    if (pending) api.initAllWidgets();
  };

  let okendoTimer = null;
  const observeResults = () => {
    document.querySelectorAll('.predictive-search-form__content').forEach((container) => {
      if (container.__archiesResultsObserved) return;
      container.__archiesResultsObserved = true;
      new MutationObserver(() => {
        clearTimeout(okendoTimer);
        okendoTimer = setTimeout(rehydrateOkendo, 300);
      }).observe(container, { childList: true, subtree: true });
    });
  };
  observeResults();
  document.addEventListener('DOMContentLoaded', observeResults);

  // Close the search popover when a result row is clicked. Horizon soft-navigates, and #handleModalClick
  // doesn't close on result clicks, so the modal / inline drawer search would otherwise stay open on
  // the PDP. Close the dialog (header/mobile modal) or collapse the inline drawer search.
  document.addEventListener('click', (event) => {
    const row = event.target.closest('.ps-row-ext');
    if (!row) return;
    const dialog = row.closest('dialog-component');
    if (dialog && typeof dialog.closeDialog === 'function') {
      dialog.closeDialog();
      return;
    }
    const drawerComp = row.closest('.predictive-search--drawer');
    if (drawerComp) collapse(drawerComp);
  });
}
